"use client";

import { useState, useEffect } from "react";
import { queryListings } from "../../services/classified";
import RentalCard from "./cards/PropertyCard";

const staticRentals = [
  { id: 1, coverPhoto: "/image/default-classified-ad.webp" },
  { id: 2, coverPhoto: "/image/default-classified-ad.webp" },
  { id: 3, coverPhoto: "/image/default-classified-ad.webp" },
  { id: 4, coverPhoto: "/image/default-classified-ad.webp" },
  { id: 5, coverPhoto: "/image/default-classified-ad.webp" },
];

export default function RentalsSection() {
  const [rentalCards, setRentalCards] = useState([]);
  const [hasData, setHasData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRentals = async () => {
      setIsLoading(true);
      try {
        const geohash = localStorage.getItem("geohash") || "9v6m";
        const response = await queryListings({
          category: "House",
          limit: 5,
          page: 1,
          geohash: geohash,
        });

        const houseCategory = response?.data?.data?.find(
          (item) => item?.category?.title === "House"
        );

        if (houseCategory?.category?.products && houseCategory.category.products.length > 0) {
          const formattedRentals = houseCategory.category.products.slice(0, 5).map((product) => {
            const detailsMap = {};
            if (product.details && Array.isArray(product.details)) {
              product.details.forEach((detail) => {
                if (detail.key && detail.value) {
                  detailsMap[detail.key] = detail.value;
                }
              });
            }

            return {
              id: product.id,
              title: product.title || "House",
              coverPhoto: product.coverPhoto || "/image/default-classified-ad.webp",
              address: `${product.fullName || ""}`.trim() || "Location TBD",
              price: parseFloat(product.price) || 0,
              beds: parseInt(detailsMap.bedrooms) || 0,
              baths: parseFloat(detailsMap.bathrooms) || 0,
              details: product.details || [],
              availableFrom: "Available now",
            };
          });
          setRentalCards(formattedRentals);
          setHasData(true);
        } else {
          setRentalCards(staticRentals);
          setHasData(false);
        }
      } catch (error) {
        console.error("Error fetching rentals:", error);
        setRentalCards(staticRentals);
        setHasData(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRentals();

    const handleGeohashChange = () => {
      fetchRentals();
    };

    window.addEventListener("geohashChanged", handleGeohashChange);
    window.addEventListener("storage", (e) => {
      if (e.key === "geohash") {
        handleGeohashChange();
      }
    });

    return () => {
      window.removeEventListener("geohashChanged", handleGeohashChange);
      window.removeEventListener("storage", handleGeohashChange);
    };
  }, []);

  return (
    <section
      className="bg-white bg-cover bg-center bg-no-repeat"
      style={{ 
        backgroundImage: "url('/assets/rentalbg.png')",
        paddingTop: 'clamp(96px, 8.33vw, 160px)',
        paddingBottom: 'clamp(96px, 8.33vw, 160px)',
        paddingLeft: 'clamp(16px, 1.25vw, 24px)',
        paddingRight: 'clamp(16px, 1.25vw, 24px)'
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@700&family=Afacad:wght@400;500;600&display=swap');
        
        .fraunces-bold {
          font-family: 'Fraunces', serif;
          font-weight: 700;
        }
        
        .afacad-font {
          font-family: 'Afacad', sans-serif;
        }
      `}</style>

      <div className="mx-auto" style={{ maxWidth: '1920px', margin: '0 auto' }}>
        {/* Header */}
        <div className="text-center" style={{ marginBottom: 'clamp(32px, 2.78vw, 54px)' }}>
          <h2
            className="fraunces-bold"
            style={{ 
              color: "#7B189F",
              fontSize: 'clamp(18px, 1.31vw, 26px)',
              fontWeight: 700,
              lineHeight: '1.2',
              marginBottom: 'clamp(12px, 0.83vw, 16px)'
            }}
          >
            Recently Uploaded House Posts
          </h2>
          <p className="text-gray-600 max-w-xl sm:max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto px-4 fraunces-bold" style={{ 
            fontSize: 'clamp(16px, 1.11vw, 22px)', 
            fontWeight: 700,
            lineHeight: '1.4',
            marginTop: 0
          }}>
            Explore hand-picked houses curated just for you.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#7B189F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg font-medium afacad-font">Loading houses...</p>
            </div>
          </div>
        ) : hasData ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5 gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-7 2xl:gap-8">
            {rentalCards.slice(0, 5).map((rental) => (
              <RentalCard
                key={`rental-${rental.id}`}
                title={rental.title}
                coverPhoto={rental.coverPhoto}
                address={rental.address}
                price={rental.price}
                beds={rental.beds}
                baths={rental.baths}
                details={rental.details}
                availableFrom={rental.availableFrom}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5 gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-7 2xl:gap-8">
            {rentalCards.slice(0, 5).map((rental) => (
              <div
                key={`empty-${rental.id}`}
                className="w-full rounded-xl overflow-hidden bg-white shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
              >
                <img
                  src={rental.coverPhoto || "/image/default-classified-ad.webp"}
                  alt="House"
                  className="w-full h-full object-cover"
                  style={{ aspectRatio: '244/180' }}
                />
              </div>
            ))}
          </div>
        )}

        {/* See More Link */}
        <div className="text-center" style={{ marginTop: 'clamp(32px, 2.78vw, 54px)' }}>
          <button
            className="afacad-font hover:opacity-80 flex items-center justify-center mx-auto transition-opacity gap-2"
            style={{ 
              color: "#411B26",
              fontSize: 'clamp(12px, 0.83vw, 16px)',
              fontWeight: 600
            }}
          >
            See More
            <img
              src="/landing/more.png"
              alt="More"
              style={{ width: 'clamp(30px, 2.17vw, 43px)', height: 'clamp(30px, 2.17vw, 43px)' }}
            />
          </button>
        </div>
      </div>
    </section>
  );
}
